import React, { type FC, useMemo } from 'react';
import { connect } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace, NAMESPACE_INTERESTING_MATCHES } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { IconSize, LiveIndicator } from 'app/design-system/components/LiveIndicator';
import { MatchObjects, MatchStatus, MatchType, SlotType } from 'app/lib/betting/betting-entities';
import { type SportId, TEAMS } from 'app/lib/betting/constants';

import MatchCenterLink from '../MatchCenterLink';
import { getMatchInformationComponent } from './components/MatchInformations';
import { MatchObjectCell } from './components/MatchObjectCell';
import { MatchStartTime } from './components/MatchStartTime';
import { MatchStreamingCtrl } from './components/MatchStreamingCtrl';
import { getMatchScoreboardCellsComponent } from './components/ScoreboardCells';

import styles from './MatchDetails.module.scss';

// ---

type MatchDetailsProps = {
  matchId: number;
};

type StoreProps = {
  objects: MatchObjects;
  matchType?: MatchType;
  matchName?: string;
  sportId?: SportId;
  startDate?: Date;
  isLive: boolean;
  isStarted: boolean;
  isMatchNotStarted: boolean;
  namespace: ContextNamespace;
};

const MatchDetailsWithoutHOC: FC<MatchDetailsProps & StoreProps> = ({
  matchId,
  objects,
  matchType,
  matchName,
  sportId,
  startDate,
  isLive,
  isStarted,
  isMatchNotStarted,
  namespace,
}) => {
  const renderScoreboardCells = useMemo(() => {
    if (isStarted) {
      const MatchScoreboardCells = getMatchScoreboardCellsComponent(sportId);

      return (
        <MatchScoreboardCells matchId={matchId} />
      );
    }
  }, [sportId, matchId]);

  const renderMatchInformation = useMemo(() => {
    if (isLive) {
      const MatchInformation = getMatchInformationComponent(sportId, isMatchNotStarted);

      return (
        <MatchInformation matchId={matchId} />
      );
    }
  }, [sportId, isMatchNotStarted, matchId]);

  return (
    <>
      {matchType === MatchType.HEAD_TO_HEAD && (
        <MatchCenterLink className={styles.matchDetailsContainer}
                         matchId={matchId}>
          <ul className={styles.matchTitle}
              data-test="match-title">
            {TEAMS.map((teamId) => (
              <MatchObjectCell key={teamId}
                               matchName={objects.get(teamId)?.get('name')} />
            ))}
          </ul>

          {isStarted && (
            <ul className={styles.matchScores}>
              {renderScoreboardCells}
            </ul>
          )}
        </MatchCenterLink>
      )}

      {matchType === MatchType.OUTRIGHT && (
        <MatchCenterLink matchId={matchId}
                         className={styles.singleEvent}>
          {matchName}
        </MatchCenterLink>
      )}

      <div className={styles.matchInformation}>
        {(namespace === NAMESPACE_INTERESTING_MATCHES && isLive) && (
          <LiveIndicator className={styles.liveIndicatorIcon}
                         size={IconSize.SMALL} />
        )}

        {isLive
          ? renderMatchInformation
          : <MatchStartTime value={startDate} />
        }

        <MatchStreamingCtrl matchId={matchId} />
      </div>
    </>
  );
};

const createMapStateToProps = (_: ReduxState, {
  $betting: {
    namespace,
    content: {
      selectors: {
        matches,
        createMatchObjectsSelector,
        isMatchStarted,
      },
    },
  },
  matchId,
}: BettingContextProps) => {
  const matchObjects = createMatchObjectsSelector(matchId);

  return (state: ReduxState) => {
    const match = matches(state).get(matchId);
    const isLive = match?.slotType === SlotType.LIVE;
    const isStarted = isLive && isMatchStarted(state, { matchId });
    const isMatchNotStarted = isLive && match.status === MatchStatus.NOT_STARTED;

    return {
      objects: matchObjects(state),
      matchType: match?.type,
      matchName: match?.name,
      sportId: match?.sportId,
      startDate: match?.startDate,
      isLive,
      isStarted,
      isMatchNotStarted,
      namespace,
    };
  };
};

export const MatchDetails = compose<FC<MatchDetailsProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(MatchDetailsWithoutHOC);
